import { useState } from "react";
import { X } from "lucide-react";
import { EMPTY_PRODUCT, CATEGORIES, CONDITIONS, COLOURS, HARDWARE, ACCOMPANIED, COMMON_SIZES, gbp, compressImage } from "./constants";

// ============================================================
// LISTING FORM — helpers hoisted OUT of ListingForm to avoid
// component-recreation on every keystroke (which was causing
// inputs to lose focus and scroll to top).
// ============================================================

const Chip = ({ selected, onClick, children, testid }) => (
    <button
        type="button"
        onClick={onClick}
        data-testid={testid}
        style={{
            padding: "8px 14px",
            fontFamily: "var(--font-sans)",
            fontSize: 11,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            background: selected ? "var(--cocoa)" : "transparent",
            color: selected ? "var(--ivory)" : "var(--cocoa)",
            border: `1px solid ${selected ? "var(--cocoa)" : "var(--sand)"}`,
            cursor: "pointer",
            transition: "background 0.2s ease, color 0.2s ease",
        }}
    >
        {children}
    </button>
);

const StepPanel = ({ title, subtitle, children }) => (
    <section style={{ background: "var(--parchment)", border: "1px solid var(--sand-line)", padding: 28, marginBottom: 20 }}>
        <div style={{ marginBottom: 20 }}>
            <h3 style={{ fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: "1.6rem", color: "var(--cocoa)", margin: 0 }}>{title}</h3>
            {subtitle && <p style={{ margin: "6px 0 0", color: "var(--cocoa)", opacity: 0.75, fontFamily: "var(--font-serif)", fontSize: 14 }}>{subtitle}</p>}
        </div>
        {children}
    </section>
);

const STEPS = [
    { n: 1, label: "Photos & Basics" },
    { n: 2, label: "Details" },
    { n: 3, label: "Provenance" },
    { n: 4, label: "Publish" },
];

function ListingProgress({ current, onJump }) {
    const pct = ((current - 1) / (STEPS.length - 1)) * 100;
    return (
        <div data-testid="listing-progress" style={{ marginBottom: 24 }}>
            <div style={{ position: "relative", height: 2, background: "var(--sand-line)", marginBottom: 14 }}>
                <div style={{ position: "absolute", top: 0, left: 0, height: 2, width: `${pct}%`, background: "var(--cocoa)", transition: "width 0.35s ease" }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                {STEPS.map((s) => {
                    const done = s.n < current;
                    const active = s.n === current;
                    return (
                        <button
                            key={s.n}
                            type="button"
                            onClick={() => onJump(s.n)}
                            data-testid={`listing-step-${s.n}`}
                            style={{
                                display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
                                background: "transparent", border: "none", cursor: "pointer", padding: 0, flex: 1,
                            }}
                        >
                            <span style={{
                                width: 30, height: 30, borderRadius: "50%",
                                display: "inline-flex", alignItems: "center", justifyContent: "center",
                                background: (active || done) ? "var(--cocoa)" : "var(--parchment)",
                                color: (active || done) ? "var(--ivory)" : "var(--cocoa)",
                                border: `1px solid ${(active || done) ? "var(--cocoa)" : "var(--sand)"}`,
                                fontFamily: "var(--font-serif)", fontSize: 13, fontStyle: "italic",
                            }}>
                                {done ? "✓" : s.n}
                            </span>
                            <span style={{
                                fontFamily: "var(--font-sans)", fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase",
                                color: active ? "var(--cocoa)" : "var(--cocoa-soft)",
                                fontWeight: active ? 600 : 400,
                            }}>
                                {s.label}
                            </span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

export default function ListingForm({ initial, onSave, onCancel }) {
    const [step, setStep] = useState(1);
    const [form, setForm] = useState({
        ...EMPTY_PRODUCT,
        ...initial,
        accompanied_by: Array.isArray(initial.accompanied_by) ? initial.accompanied_by : [],
        images: Array.isArray(initial.images) ? initial.images : [],
    });
    const [uploading, setUploading] = useState(false);
    const [dragIdx, setDragIdx] = useState(null);
    const [err, setErr] = useState("");
    const [saving, setSaving] = useState(false);

    const setF = (patch) => setForm((f) => ({ ...f, ...patch }));

    const onFiles = async (files) => {
        setErr("");
        const list = Array.from(files);
        for (const f of list) {
            if (!f.type.startsWith("image/")) { setErr("Only image files (JPG, PNG, WEBP, HEIC) are supported."); return; }
            if (f.size > 15 * 1024 * 1024) { setErr(`${f.name} is over 15MB. Please choose a smaller file.`); return; }
        }
        setUploading(true);
        try {
            const compressed = [];
            for (const f of list.slice(0, 8 - form.images.length)) {
                compressed.push(await compressImage(f));
            }
            setF({ images: [...form.images, ...compressed].slice(0, 8) });
        } catch (e) {
            setErr("Failed to process one of the images. Please try a different file.");
        } finally {
            setUploading(false);
        }
    };

    const removeImg = (i) => setF({ images: form.images.filter((_, x) => x !== i) });
    const moveImg = (from, to) => {
        if (from === to || to < 0 || to >= form.images.length) return;
        const arr = [...form.images];
        const [x] = arr.splice(from, 1);
        arr.splice(to, 0, x);
        setF({ images: arr });
    };

    const toggleAcc = (v) => setF({
        accompanied_by: form.accompanied_by.includes(v)
            ? form.accompanied_by.filter((x) => x !== v)
            : [...form.accompanied_by, v],
    });

    const sizesForCategory = COMMON_SIZES[form.category] || COMMON_SIZES.default;

    // Step validation — required fields per step so admin can't publish incomplete listings
    const validateStep = (n) => {
        if (n === 1) {
            if (!form.title.trim()) return "Please add a title.";
            if (!form.designer.trim()) return "Designer is required.";
            if (!form.price || Number(form.price) <= 0) return "Please enter a selling price.";
        }
        if (n === 2) {
            if (!form.category) return "Please choose a category.";
        }
        return "";
    };

    const goNext = () => {
        const msg = validateStep(step);
        if (msg) { setErr(msg); return; }
        setErr("");
        setStep((s) => Math.min(STEPS.length, s + 1));
        window.scrollTo({ top: 0, behavior: "smooth" });
    };
    const goBack = () => {
        setErr("");
        setStep((s) => Math.max(1, s - 1));
        window.scrollTo({ top: 0, behavior: "smooth" });
    };
    const jumpTo = (n) => {
        // Allow going back freely, only forward if previous steps validate
        if (n < step) { setErr(""); setStep(n); return; }
        for (let i = step; i < n; i++) {
            const msg = validateStep(i);
            if (msg) { setErr(msg); return; }
        }
        setErr("");
        setStep(n);
    };

    const doSave = async (publish) => {
        setErr("");
        // Full validation
        for (let i = 1; i <= STEPS.length; i++) {
            const msg = validateStep(i);
            if (msg) { setErr(msg); setStep(i); return; }
        }
        const status = publish ? (form.status === "draft" ? "live" : form.status) : "draft";
        if (status === "live" && form.images.length === 0) {
            setErr("Please add at least one photo before publishing (or save as draft).");
            setStep(1);
            return;
        }
        setSaving(true);
        try {
            await onSave({ ...form, status });
        } catch (e) {
            setErr(e?.response?.data?.detail || "Something went wrong saving the listing.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={(e) => { e.preventDefault(); if (step < STEPS.length) goNext(); else doSave(true); }} data-testid="admin-listing-form">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, position: "sticky", top: 0, zIndex: 10, background: "var(--parchment-soft)", padding: "16px 0", borderBottom: "1px solid var(--sand-line)" }}>
                <div>
                    <h2 style={{ fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: "1.6rem", margin: 0, color: "var(--cocoa)" }}>
                        {form.id ? "Edit piece" : "New piece"}
                    </h2>
                    {form.sku && <div style={{ fontFamily: "monospace", fontSize: 11, color: "var(--cocoa-soft)", marginTop: 4 }}>SKU {form.sku}</div>}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                    <button type="button" className="btn btn-outline" onClick={onCancel} data-testid="admin-listing-cancel">Cancel</button>
                    <button type="button" className="btn btn-outline" onClick={() => doSave(false)} disabled={saving} data-testid="admin-listing-save-draft">
                        {saving ? "Saving…" : "Save as Draft"}
                    </button>
                </div>
            </div>

            <ListingProgress current={step} onJump={jumpTo} />

            {err && <div className="form-error" style={{ marginBottom: 16 }} data-testid="listing-form-error">{err}</div>}

            {step === 1 && (
                <>
                    <StepPanel title="Photos" subtitle="Up to 8 shots — first image is the primary. Drag to reorder.">
                        <label
                            className="upload-dropzone"
                            data-testid="listing-image-drop"
                            onDragOver={(e) => { e.preventDefault(); }}
                            onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files.length) onFiles(e.dataTransfer.files); }}
                            style={{ marginBottom: form.images.length ? 16 : 0 }}
                        >
                            {uploading ? "Processing…" : "Drop photos here or click to choose — JPG · PNG · HEIC · max 15MB each"}
                            <input type="file" accept="image/*" multiple style={{ display: "none" }} onChange={(e) => onFiles(e.target.files)} disabled={form.images.length >= 8} />
                        </label>
                        {form.images.length > 0 && (
                            <div className="upload-previews">
                                {form.images.map((src, i) => (
                                    <div
                                        key={i}
                                        className="upload-preview"
                                        data-testid={`listing-image-${i}`}
                                        draggable
                                        onDragStart={() => setDragIdx(i)}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={() => { if (dragIdx !== null) moveImg(dragIdx, i); setDragIdx(null); }}
                                        style={{ cursor: "grab", opacity: dragIdx === i ? 0.4 : 1 }}
                                    >
                                        <img src={src} alt="" />
                                        {i === 0 && <span style={{ position: "absolute", top: 4, left: 4, background: "rgba(240,226,192,0.95)", fontSize: 9, padding: "2px 6px", letterSpacing: "0.2em", textTransform: "uppercase" }}>Primary</span>}
                                        <button type="button" onClick={() => removeImg(i)} aria-label="Remove"><X size={12} strokeWidth={1.6} /></button>
                                        <div style={{ position: "absolute", bottom: 4, left: 4, display: "flex", gap: 2 }}>
                                            <button type="button" onClick={() => moveImg(i, i - 1)} disabled={i === 0} style={{ background: "rgba(28,12,6,0.72)", color: "var(--ivory)", border: "none", width: 20, height: 20, cursor: i === 0 ? "not-allowed" : "pointer", fontSize: 11 }}>◀</button>
                                            <button type="button" onClick={() => moveImg(i, i + 1)} disabled={i === form.images.length - 1} style={{ background: "rgba(28,12,6,0.72)", color: "var(--ivory)", border: "none", width: 20, height: 20, cursor: i === form.images.length - 1 ? "not-allowed" : "pointer", fontSize: 11 }}>▶</button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div style={{ marginTop: 8, fontFamily: "var(--font-serif)", fontStyle: "italic", color: "var(--cocoa)", opacity: 0.75, fontSize: 13 }}>
                            {form.images.length}/8 photos
                        </div>
                    </StepPanel>

                    <StepPanel title="The Basics" subtitle="Title, designer and price.">
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                            <div className="field" style={{ margin: 0 }}><label className="label">Title *</label><input className="input" required value={form.title} onChange={(e) => setF({ title: e.target.value })} data-testid="listing-title" placeholder="Double-Breasted Wool Coat" /></div>
                            <div className="field" style={{ margin: 0 }}><label className="label">Designer *</label><input className="input" required value={form.designer} onChange={(e) => setF({ designer: e.target.value })} data-testid="listing-designer" placeholder="The Row" /></div>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                            <div className="field" style={{ margin: 0 }}><label className="label">Selling price (£) *</label><input className="input" type="number" step="1" required value={form.price} onChange={(e) => setF({ price: e.target.value })} data-testid="listing-price" placeholder="1250" /></div>
                            <div className="field" style={{ margin: 0 }}><label className="label">Original retail (£)</label><input className="input" type="number" step="1" value={form.original_price || ""} onChange={(e) => setF({ original_price: e.target.value })} placeholder="Shows as strike-through" /></div>
                        </div>
                    </StepPanel>
                </>
            )}

            {step === 2 && (
                <>
                    <StepPanel title="Category & Size" subtitle="What is it, and how does it fit?">
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                            <div className="field" style={{ margin: 0 }}>
                                <label className="label">Category *</label>
                                <select className="input" value={form.category} onChange={(e) => setF({ category: e.target.value, size: "" })} data-testid="listing-category">
                                    {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                                </select>
                            </div>
                            <div className="field" style={{ margin: 0 }}><label className="label">Subcategory</label><input className="input" value={form.subcategory} onChange={(e) => setF({ subcategory: e.target.value })} placeholder="e.g. Flap Bag, Trench" /></div>
                        </div>
                        <div className="field">
                            <label className="label">Size — quick picks for {form.category}</label>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                                {sizesForCategory.map((s) => (
                                    <Chip key={s} selected={form.size === s} onClick={() => setF({ size: s })} testid={`chip-size-${s}`}>{s}</Chip>
                                ))}
                            </div>
                            <input className="input" value={form.size} onChange={(e) => setF({ size: e.target.value })} placeholder="Or type a custom size" data-testid="listing-size" />
                        </div>
                        <div className="field">
                            <label className="label">Measurements</label>
                            <input className="input" value={form.measurements} onChange={(e) => setF({ measurements: e.target.value })} placeholder="Shoulder 39cm · Chest 44cm · Length 108cm" />
                        </div>
                    </StepPanel>

                    <StepPanel title="Condition & Story" subtitle="Be honest — direct disclosure builds trust.">
                        <div className="field">
                            <label className="label">Condition</label>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                {CONDITIONS.map((c) => (
                                    <Chip key={c} selected={form.condition === c} onClick={() => setF({ condition: c })} testid={`chip-condition-${c}`}>{c}</Chip>
                                ))}
                                <Chip selected={form.new_with_tags} onClick={() => setF({ new_with_tags: !form.new_with_tags })} testid="chip-nwt">New with tags</Chip>
                            </div>
                        </div>
                        <div className="field">
                            <label className="label">Description</label>
                            <textarea className="input" rows="4" value={form.description} onChange={(e) => setF({ description: e.target.value })} placeholder="Tell the story of the piece — cut, provenance, era. Direct honesty about any wear." data-testid="listing-description" />
                        </div>
                    </StepPanel>
                </>
            )}

            {step === 3 && (
                <StepPanel title="Colour, Material & Provenance" subtitle="Details that separate a great listing from a good one.">
                    <div className="field">
                        <label className="label">Colour — quick picks</label>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                            {COLOURS.map((c) => (
                                <Chip key={c} selected={form.color === c} onClick={() => setF({ color: c })} testid={`chip-color-${c}`}>{c}</Chip>
                            ))}
                        </div>
                        <input className="input" value={form.color} onChange={(e) => setF({ color: e.target.value })} placeholder="Or a specific shade — e.g. Warm Camel" data-testid="listing-color" />
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                        <div className="field" style={{ margin: 0 }}><label className="label">Material</label><input className="input" value={form.material} onChange={(e) => setF({ material: e.target.value })} placeholder="Lambskin, Wool, Silk, Cashmere…" /></div>
                        <div className="field" style={{ margin: 0 }}>
                            <label className="label">Hardware</label>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
                                {HARDWARE.map((h) => (
                                    <Chip key={h} selected={form.hardware === h} onClick={() => setF({ hardware: h })}>{h}</Chip>
                                ))}
                            </div>
                            <input className="input" value={form.hardware} onChange={(e) => setF({ hardware: e.target.value })} placeholder="Or custom" />
                        </div>
                        <div className="field" style={{ margin: 0 }}><label className="label">Interior</label><input className="input" value={form.interior} onChange={(e) => setF({ interior: e.target.value })} placeholder="Leather, Grosgrain, Silk lining…" /></div>
                        <div className="field" style={{ margin: 0 }}><label className="label">Country of origin</label><input className="input" value={form.country_of_origin} onChange={(e) => setF({ country_of_origin: e.target.value })} placeholder="Italy, France…" /></div>
                        <div className="field" style={{ margin: 0 }}><label className="label">Serial / Model number</label><input className="input" value={form.serial_number} onChange={(e) => setF({ serial_number: e.target.value })} placeholder="If applicable" /></div>
                    </div>
                    <div className="field" style={{ marginTop: 12 }}>
                        <label className="label">Accompanied by</label>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                            {ACCOMPANIED.map((a) => (
                                <Chip key={a} selected={form.accompanied_by.includes(a)} onClick={() => toggleAcc(a)} testid={`chip-acc-${a.replace(/\s+/g, "-").toLowerCase()}`}>{a}</Chip>
                            ))}
                        </div>
                    </div>
                </StepPanel>
            )}

            {step === 4 && (
                <>
                    <StepPanel title="Review" subtitle="Last look before it goes live.">
                        <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 24, alignItems: "start" }}>
                            {form.images[0] ? (
                                <img src={form.images[0]} alt="" style={{ width: 160, height: 200, objectFit: "cover", border: "1px solid var(--sand-line)" }} />
                            ) : (
                                <div style={{ width: 160, height: 200, background: "var(--parchment-soft)", border: "1px dashed var(--sand)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--cocoa-soft)", fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: 12, textAlign: "center", padding: 12 }}>
                                    No photo yet
                                </div>
                            )}
                            <div style={{ color: "var(--cocoa)" }}>
                                <div style={{ fontFamily: "var(--font-serif)", fontSize: "1.4rem", fontStyle: "italic" }}>{form.title || "Untitled piece"}</div>
                                <div style={{ fontFamily: "var(--font-sans)", fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase", marginTop: 4, color: "var(--cocoa-soft)" }}>{form.designer || "—"}</div>
                                <div style={{ fontFamily: "var(--font-serif)", fontSize: "1.2rem", marginTop: 12 }}>{form.price ? gbp(form.price) : "—"}{form.original_price ? <span style={{ marginLeft: 8, color: "var(--cocoa-soft)", textDecoration: "line-through", fontSize: "0.9rem" }}>{gbp(form.original_price)}</span> : null}</div>
                                <div style={{ marginTop: 12, fontFamily: "var(--font-serif)", color: "var(--cocoa)", fontSize: 14 }}>
                                    {form.category}{form.subcategory ? ` · ${form.subcategory}` : ""} · Size {form.size || "—"} · {form.condition}
                                </div>
                                {form.color && <div style={{ marginTop: 4, fontFamily: "var(--font-serif)", color: "var(--cocoa)", fontSize: 14 }}>Colour {form.color}</div>}
                                {form.accompanied_by.length > 0 && <div style={{ marginTop: 4, fontFamily: "var(--font-serif)", color: "var(--cocoa)", fontSize: 14 }}>With: {form.accompanied_by.join(", ")}</div>}
                            </div>
                        </div>
                    </StepPanel>

                    <StepPanel title="Placement & Publish" subtitle="Set the status, tag it into an edit, then hit publish.">
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                            <div className="field" style={{ margin: 0 }}>
                                <label className="label">Edit collection</label>
                                <select className="input" value={form.edit_slug || ""} onChange={(e) => setF({ edit_slug: e.target.value })}>
                                    <option value="">— none —</option>
                                    <option value="the-tailoring-edit">The Tailoring Edit</option>
                                    <option value="the-outerwear-edit">The Outerwear Edit</option>
                                    <option value="the-quiet-luxury-edit">The Quiet Luxury Edit</option>
                                    <option value="the-accessory-edit">The Accessory Edit</option>
                                </select>
                            </div>
                            <div className="field" style={{ margin: 0 }}>
                                <label className="label">Status</label>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                    {["draft", "live", "reserved", "sold", "archived"].map((s) => (
                                        <Chip key={s} selected={form.status === s} onClick={() => setF({ status: s })} testid={`chip-status-${s}`}>{s}</Chip>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="field" style={{ marginTop: 12 }}>
                            <label className="label">Tags (comma-separated)</label>
                            <input className="input" value={form.tags || ""} onChange={(e) => setF({ tags: e.target.value })} placeholder="quiet-luxury, iconic, limited" />
                        </div>
                    </StepPanel>
                </>
            )}

            {/* Sticky bottom nav */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 24, padding: "20px 0", borderTop: "1px solid var(--sand-line)", position: "sticky", bottom: 0, background: "var(--parchment-soft)", zIndex: 5 }}>
                <button type="button" className="btn btn-outline" onClick={goBack} disabled={step === 1} data-testid="listing-back" style={{ opacity: step === 1 ? 0.4 : 1 }}>
                    ← Back
                </button>
                <div style={{ fontFamily: "var(--font-serif)", fontStyle: "italic", color: "var(--cocoa)", fontSize: 14 }}>
                    Step {step} of {STEPS.length} — {STEPS[step - 1].label}
                </div>
                {step < STEPS.length ? (
                    <button type="button" className="btn" onClick={goNext} data-testid="listing-next">
                        Next →
                    </button>
                ) : (
                    <button type="button" className="btn" onClick={() => doSave(true)} disabled={saving} data-testid="admin-listing-save">
                        {saving ? "Publishing…" : "Publish listing"}
                    </button>
                )}
            </div>
        </form>
    );
}
